import React, { useContext, useState } from 'react';
import { Typography, Button, Card, Empty, Modal, Tabs, Checkbox, message } from 'antd';
import AppContext from '../context/AppContext';

const { Title } = Typography;
const { Meta } = Card;

const History = () => {
  const { state, dispatch } = useContext(AppContext);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [videoId, setVideoId] = useState(null);

  const handleClearHistory = () => {
    Modal.confirm({
      title: 'Clear History',
      content: 'Are you sure you want to clear all history?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        dispatch({
          type: 'CLEAR_HISTORY'
        });
        message.success('History cleared');
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCardClick = async (recipe) => {
    setSelectedIngredients([]);
    try {
      const response = await fetch(
        `https://www.themealdb.com/api/json/v1/1/lookup.php?i=${recipe.idMeal}`
      );
      const data = await response.json();
      const meal = data.meals[0];
      
      // Extract ingredients and measures
      const mealIngredients = [];
      for (let i = 1; i <= 20; i++) {
        const ingredient = meal[`strIngredient${i}`];
        const measure = meal[`strMeasure${i}`];
        if (ingredient && ingredient.trim()) {
          mealIngredients.push({
            id: i,
            name: ingredient,
            measure: measure,
          });
        }
      }
      
      setIngredients(mealIngredients);
      setSelectedMeal(meal);
      setModalVisible(true);

      // Extract video ID if available
      if (meal.strYoutube) {
        const videoId = meal.strYoutube.split('v=')[1]?.split('&')[0];
        setVideoId(videoId);
      } else {
        setVideoId(null);
      }
    } catch (error) {
      console.error('Error fetching meal details:', error);
      message.error('Failed to load meal details');
    }
  };

  const handleIngredientSelect = (ingredientId) => {
    setSelectedIngredients(prev => {
      const isSelected = prev.includes(ingredientId);
      if (isSelected) {
        return prev.filter(id => id !== ingredientId);
      } else {
        return [...prev, ingredientId];
      }
    });
  };

  const handleAddToShoppingList = () => {
    const itemsToAdd = ingredients
      .filter(ing => selectedIngredients.includes(ing.id))
      .map(ing => ({
        id: `${selectedMeal.idMeal}-${ing.id}`,
        name: ing.name,
        measure: ing.measure,
        mealName: selectedMeal.strMeal
      }));

    if (itemsToAdd.length === 0) {
      message.warning('Please select ingredients first');
      return;
    }

    itemsToAdd.forEach(item => {
      dispatch({
        type: 'ADD_ITEM',
        payload: item
      });
    });

    message.success('Items added to shopping list');
    setModalVisible(false);
  };

  const modalTabs = [
    {
      key: 'ingredients',
      label: 'Ingredients',
      children: (
        <div style={{ paddingTop: '1rem' }}>
          <div style={{ maxHeight: '50vh', overflowY: 'auto', marginBottom: 16 }}>
            {ingredients.map((ingredient) => (
              <div key={ingredient.id} style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={selectedIngredients.includes(ingredient.id)}
                  onChange={() => handleIngredientSelect(ingredient.id)}
                >
                  {ingredient.name} - {ingredient.measure}
                </Checkbox>
              </div>
            ))}
          </div>
          <div style={{ 
            borderTop: '1px solid #f0f0f0', 
            paddingTop: 16,
            textAlign: 'right' 
          }}>
            <Button 
              type="primary" 
              onClick={handleAddToShoppingList}
            >
              Add to Shopping List
            </Button>
          </div>
        </div>
      )
    },
    {
      key: 'recipe',
      label: 'Recipe',
      children: (
        <div style={{ 
          maxHeight: '60vh', 
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {videoId && (
            <div style={{ 
              position: 'relative',
              paddingBottom: '56.25%',
              height: 0,
              overflow: 'hidden',
              marginBottom: '20px'
            }}>
              <iframe
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                src={`https://www.youtube.com/embed/${videoId}`}
                title={`Recipe video for ${selectedMeal?.strMeal}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
          <div style={{ whiteSpace: 'pre-line' }}>
            <Title level={4}>Instructions</Title>
            {selectedMeal?.strInstructions}
          </div>
        </div>
      )
    }
  ];

  if (!state.recipeHistory || state.recipeHistory.length === 0) {
    return (
      <div style={{ padding: '16px' }}>
        <Title level={4}>History</Title>
        <Empty 
          description="No recipe history yet"
          style={{ margin: '50px 0' }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <Title level={4} style={{ margin: 0 }}>History</Title>
        <Button 
          type="primary" 
          danger 
          onClick={handleClearHistory}
        >
          Clear History
        </Button>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px'
      }}>
        {state.recipeHistory.map((recipe) => (
          <Card
            key={`${recipe.idMeal}-${recipe.timestamp}`}
            hoverable
            cover={
              <img
                alt={recipe.strMeal}
                src={recipe.strMealThumb}
                style={{ height: 150, objectFit: 'cover' }}
              />
            }
            style={{ height: '100%' }}
            onClick={() => handleCardClick(recipe)}
          >
            <Meta
              title={recipe.strMeal}
              description={formatDate(recipe.timestamp)}
            />
          </Card>
        ))}
      </div>

      <Modal
        title={selectedMeal?.strMeal}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        styles={{
          body: {
            padding: '24px'
          }
        }}
      >
        <Tabs 
          items={modalTabs}
          defaultActiveKey="ingredients"
          style={{ minHeight: '300px' }}
        />
      </Modal>
    </div>
  );
};

export default History; 